import { assignMedicalUserToPatient, getMedicalUserByRole } from "@/utils/apis/medicaluser-service";
import { useAuthStore } from "@/utils/stores/useAuthStore";
import { getUserForceAssignment, isEmailExcluded } from "../../venere/features/experts/config/nutritionistAssignment";
import { ExpertRole } from "../types/ExpertRole";
import { createExpertFromCalendlyExpert } from "../utils/createExpertFromCalendlyExpert";
import { getRandomExpert } from "../utils/getRandomExpert";

/**
 * Selects the best nutritionist for a user based on assignment rules
 */
const selectNutritionist = (
  nutritionists: any[],
  userId: string
): any | null => {
  if (!nutritionists || nutritionists.length === 0) {
    console.warn('[NUTRITIONIST-SELECTION]: No nutritionists available');
    return null;
  }

  // Check for force assignments first
  const forceAssignment = getUserForceAssignment(userId);

  if (forceAssignment) {
    console.log(`[NUTRITIONIST-SELECTION]: Checking force assignment for user ${userId}`);
    
    const preferredExpert = nutritionists.find(
      nutri => nutri.email === forceAssignment.preferredEmail
    );

    if (preferredExpert) {
      console.log(`[NUTRITIONIST-SELECTION]: Found preferred expert ${preferredExpert.data.name} ${preferredExpert.data.family_name}`);
      return preferredExpert;
    } else {
      console.warn(`[NUTRITIONIST-SELECTION]: Preferred expert ${forceAssignment.preferredEmail} not found`);
      
      if (forceAssignment.fallbackToRandom) {
        console.log('[NUTRITIONIST-SELECTION]: Falling back to random selection');
        const eligibleNutritionists = nutritionists.filter(
          nutri => !isEmailExcluded(nutri.email)
        );
        return getRandomExpert(eligibleNutritionists);
      } else {
        console.error('[NUTRITIONIST-SELECTION]: No fallback allowed, no expert selected');
        return null;
      }
    }
  }

  // Normal random selection for other users
  console.log(`[NUTRITIONIST-SELECTION]: Using random selection for user ${userId}`);
  const eligibleNutritionists = nutritionists.filter(
    nutri => !isEmailExcluded(nutri.email)
  );

  if (eligibleNutritionists.length === 0) {
    console.warn('[NUTRITIONIST-SELECTION]: No eligible nutritionists found after filtering');
    return null;
  }

  return getRandomExpert(eligibleNutritionists);
};

const useAssignNutritionist = () => {
    const { auth } = useAuthStore()
    const idToken = auth.idToken
    const userId = auth.userId

    const assignNutri = async (onSuccess?: () => void, onError?: () => void) => {
        if (!idToken || !userId) return;

        try {
            const nutritionists = await getMedicalUserByRole(idToken, ExpertRole.Nutritionist);

            if (nutritionists && nutritionists.length > 0) {
                const selectedNutritionist = selectNutritionist(nutritionists, userId);

                if (selectedNutritionist) {
                    const newExpert = createExpertFromCalendlyExpert(selectedNutritionist);
                    assignNutritionist(newExpert.id!, `${selectedNutritionist.data.name} ${selectedNutritionist.data.family_name}`, onSuccess, onError);
                } else {
                    console.warn("No nutritionist selected.");
                    onError?.();
                }
            } else {
                console.warn("No nutritionists found.");
                onError?.();
            }
        } catch (error) {
            console.error("Error fetching nutritionists:", error);
            onError?.();
        }
    };

    const assignNutritionist = async (nutriId: string, nutriName: string, onSuccess?: () => void, onError?: () => void) => {
        if (!userId || !idToken) return;

        const nutri = await assignMedicalUserToPatient(
            nutriId,
            userId,
            idToken,
        )
        if (nutri) {
            console.log(`[NUTRITIONIST]: ${nutriName} assigned to user=${userId}`)
            onSuccess?.()
        } else {
            console.error("Nutritionist not assigned");
            onError?.();
        }
    };

    return { assignNutri };
};

export default useAssignNutritionist;